'use client';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { uniqueId } from '@paypalcorp/pp-react-utils';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Animator = function (_ref) {
  var _ref$id = _ref.id,
    id = _ref$id === void 0 ? uniqueId('animatee#') : _ref$id,
    _ref$shouldHide = _ref.shouldHide,
    shouldHide = _ref$shouldHide === void 0 ? false : _ref$shouldHide,
    onOpen = _ref.onOpen,
    onCloseComplete = _ref.onCloseComplete,
    _ref$phaseProps = _ref.phaseProps,
    phaseProps = _ref$phaseProps === void 0 ? {
      initial: {
        isHidden: true
      },
      showing: {
        isHidden: false
      },
      hiding: {
        isHidden: true
      }
    } : _ref$phaseProps,
    children = _ref.children;
  var _useState = useState('initial'),
    _useState2 = _slicedToArray(_useState, 2),
    currentPhase = _useState2[0],
    setCurrentPhase = _useState2[1];
  var closeEvent = useRef({
    event: null,
    closeMethod: ''
  });
  var animatee = useRef(id);
  var fallbackTimer = useRef(null);

  // If we're done hiding, tell the app that it's safe to remove us from the DOM
  var finishClose = function () {
    // istanbul ignore next
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
    /* istanbul ignore next */
    var _ref2 = closeEvent.current || {},
      event = _ref2.event,
      closeMethod = _ref2.closeMethod;
    // istanbul ignore next
    setCurrentPhase(function (oldPhase) {
      // Since we have the failsafe (see below), we must make sure
      // that we don't send the notification twice...
      if (oldPhase !== 'initial') {
        if (typeof onCloseComplete === 'function') {
          onCloseComplete(event, closeMethod);
        }
        return 'initial';
      }
      return oldPhase;
    });
  };
  useLayoutEffect(function () {
    if (!shouldHide && currentPhase === 'initial') {
      setCurrentPhase('showing');
    }
    if (shouldHide && currentPhase !== 'initial') {
      closeEvent.current.closeMethod = 'caller';
      setCurrentPhase('hiding');
    }
  }, [shouldHide]);
  var onAnimationEnd = function (evt) {
    /* istanbul ignore else */
    if (evt.target.dataset.animatee === animatee.current) {
      if (currentPhase === 'hiding') {
        finishClose();
      } else if (typeof onOpen === 'function') {
        onOpen(evt); // Let app know entrance is complete and we're ready for action
      }
      evt.stopPropagation();
      evt.nativeEvent.stopImmediatePropagation();
    }
  };
  if (shouldHide && currentPhase === 'initial') {
    return null;
  }
  var childProps = _objectSpread({
    requestClose: function requestClose(evt, closedBy) {
      closeEvent.current.event = evt;
      closeEvent.current.closeMethod = closedBy;
      setCurrentPhase('hiding');
      // Failsafe here in case the closing animation event gets dropped
      // or stomped upon. This mostly happens on Chrome but has been
      // seen on all browsers with rapid clicking of open/close
      // https://paypal.atlassian.net/browse/DTPAYPALUI-15339
      // --Love, BCC
      /* istanbul ignore next */
      fallbackTimer.current = setTimeout(function () {
        finishClose();
      }, 1000);
    },
    'data-animatee': animatee.current
  }, phaseProps[currentPhase]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "animator",
    onTransitionEnd: onAnimationEnd,
    onAnimationEnd: onAnimationEnd
  }, children(childProps));
};

export { Animator };
